import { useState } from 'react';
import { slideshowLibraryApi, SlideshowLibraryImage, uploadToS3 } from '@/lib/api';

/**
 * Upload queue for the facility's slideshow image library (1.29.0).
 *
 * Shared by the Image Library tab and a slideshow's own upload panel: every
 * upload lands in the library, and the slideshow page then adds the new
 * images to itself via `onUploaded`. Files over the site's limit are refused
 * when picked rather than after the operator presses Upload (1.28.3).
 */

export interface StagedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const readDimensions = (file: File) =>
  new Promise<{ width: number; height: number } | null>((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { resolve({ width: img.width, height: img.height }); URL.revokeObjectURL(url); };
    img.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
    img.src = url;
  });

export function useLibraryUpload(opts: {
  /** The facility whose library receives uploads. Required for admins; ignored server-side for club managers. */
  facilityId?: string | undefined;
  /** The site's per-file limit in bytes (null while unknown). */
  maxFileSize: number | null;
  /** Called once with every image that uploaded successfully in a batch. */
  onUploaded?: (images: SlideshowLibraryImage[]) => Promise<void> | void;
}) {
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const limitLabel = opts.maxFileSize ? formatFileSize(opts.maxFileSize) : null;

  const stageFiles = (files: FileList | File[]) => {
    const entries: StagedFile[] = Array.from(files).map(file =>
      opts.maxFileSize && file.size > opts.maxFileSize
        ? {
            file, status: 'error', progress: 0,
            error: `Too large — ${formatFileSize(file.size)}; the limit is ${limitLabel}. Save it smaller (e.g. JPG or compressed PNG) and try again.`,
          }
        : { file, status: 'pending', progress: 0 });
    setStaged(prev => [...prev, ...entries]);
  };

  const removeStaged = (idx: number) => setStaged(prev => prev.filter((_, i) => i !== idx));
  const clearStaged = () => setStaged([]);
  const setAt = (i: number, patch: Partial<StagedFile>) =>
    setStaged(prev => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const uploadAll = async () => {
    if (!staged.some(s => s.status === 'pending')) return;
    setUploading(true);
    const done: SlideshowLibraryImage[] = [];

    for (let i = 0; i < staged.length; i++) {
      const entry = staged[i];
      if (!entry || entry.status !== 'pending') continue;
      setAt(i, { status: 'uploading' });
      try {
        const presign = await slideshowLibraryApi.presign({
          filename: entry.file.name,
          contentType: entry.file.type,
          size: entry.file.size,
          facilityId: opts.facilityId,
        });
        await uploadToS3({ url: presign.uploadUrl, fields: presign.fields }, entry.file, pct => setAt(i, { progress: pct }));
        const dims = await readDimensions(entry.file);
        const { image } = await slideshowLibraryApi.notify({
          key: presign.key,
          filename: entry.file.name,
          originalName: entry.file.name,
          contentType: entry.file.type,
          size: entry.file.size,
          facilityId: opts.facilityId,
          ...(dims ? { width: dims.width, height: dims.height } : {}),
        });
        done.push(image);
        setAt(i, { status: 'success', progress: 100 });
      } catch (e: any) {
        setAt(i, { status: 'error', error: e?.message || 'Upload failed' });
      }
    }

    try {
      if (done.length > 0) await opts.onUploaded?.(done);
    } finally {
      setUploading(false);
      // Successes clear themselves; failures stay so the operator sees why.
      setTimeout(() => setStaged(prev => prev.filter(s => s.status !== 'success')), 2500);
    }
  };

  return { staged, uploading, limitLabel, stageFiles, removeStaged, clearStaged, uploadAll };
}
